import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import {
  RecurrenceInboxEntry,
  UpdateRecurrenceInboxEntryInput,
} from '@this+that/models';
import log from 'loglevel';

import { useUpdateRecurrenceInboxEntry as useUpdateService } from '../../services/recurrenceInboxEntries.service';
import { useInboxEntriesStore } from '../../store';
import { useCustomToast } from '../useCustomToast';

interface UseUpdateRecurrenceInboxEntryReturn {
  updateRecurrenceInboxEntry: (
    input: UpdateRecurrenceInboxEntryInput,
  ) => Promise<RecurrenceInboxEntry | null>;
  isUpdating: boolean;
}

function getRecurrenceEntriesState() {
  return useInboxEntriesStore.getState().taskManager.activeTaskGroup
    .recurrenceEntries;
}

export function useUpdateRecurrenceInboxEntry(): UseUpdateRecurrenceInboxEntryReturn {
  const { t } = useTranslation();
  const { errorMessage } = useCustomToast();

  const { updateRecurrenceInboxEntry: updateService, loading: isUpdating } =
    useUpdateService();

  const updateRecurrenceInboxEntry = useCallback(
    async (
      input: UpdateRecurrenceInboxEntryInput,
    ): Promise<RecurrenceInboxEntry | null> => {
      const {
        recurrenceInboxEntries,
        updateRecurrenceInboxEntry: updateStore,
      } = getRecurrenceEntriesState();

      const previousEntry = recurrenceInboxEntries.find(
        (e) => e.id === input.id,
      );

      if (previousEntry) {
        updateStore({ ...previousEntry, ...input });
      }

      try {
        const result = await updateService(input);
        if (result) {
          getRecurrenceEntriesState().updateRecurrenceInboxEntry(result);
          return result;
        }
        if (previousEntry) {
          getRecurrenceEntriesState().updateRecurrenceInboxEntry(previousEntry);
        }
        errorMessage(t('doBox.recurrences.updateError'));
        return null;
      } catch (error) {
        if (previousEntry) {
          getRecurrenceEntriesState().updateRecurrenceInboxEntry(previousEntry);
        }
        log.error(
          'useUpdateRecurrenceInboxEntry: failed to update recurrence:',
          error,
        );
        errorMessage(t('doBox.recurrences.updateError'));
        return null;
      }
    },
    [updateService, errorMessage, t],
  );

  return { updateRecurrenceInboxEntry, isUpdating };
}
